<template>
  <div class="flex flex-wrap">
    <contact-autocomplete
      v-model="companyName"
      :search-params="searchParams"
      big
      label="Наименование компании"
      required
      value-key="name"
      value-template="{name}"
      title-template="{name}"
      placeholder="Введите наименование/ИНН для поиска"
      class="mb-15 mt-15"
      :rules="rules"
      :clear-value-after-blur="false"
      :disabled="disabled || partDisabled"
      @change="onChangeContractor"
      @has-same="showSameContractorToast"
    />

    <div
      v-if="needShowNetworkInfo"
      class="corporation-message"
    >
      <vz-icon
        class="corporation-message-icon"
        name="information-outline"
        size="20"
        variant="white"
        no-masked-icon
      />
      Для доставки груза в торговую сеть перейдите к форме заказа «<span>В торговые сети</span>». После перехода
      информация будет сохранена
      <vz-button
        class="corporation-message-button"
        icon-name="arrow-right"
        icon-size="12"
        icon-right
        variant="public-primary"
        small
        @click="changeOrderType"
      >
        Перейти
      </vz-button>
    </div>

    <div class="corporation-requisites">
      <div>
        <vz-input
          v-if="companyName || !isINN"
          v-model="inn"
          big
          required
          type="number"
          :label="innLabel"
          :max-length="taxIdLength"
          :rules="iNNorBINorUNPvalidators"
          :disabled="disabled || partDisabled"
        />
        <contact-autocomplete
          v-else
          ref="innAutocomplete"
          :model-value="inn"
          :searchable="true"
          :label="innLabel"
          :rules="iNNorBINorUNPvalidators"
          :search-params="searchParams"
          :clear-value-after-blur="false"
          big
          value-key="inn"
          value-template="{inn}"
          title-template="{name}"
          placeholder=""
          @change="onChangeContractor"
        />
      </div>
      <div v-if="isINN && !focused">
        <vz-input
          v-model="kpp"
          big
          label="КПП"
          type="number"
          :max-length="9"
          :rules="[formRules.kpp]"
          :disabled="disabled || partDisabled"
        />
      </div>
    </div>
    <vz-autocomplete
      v-model="name"
      big
      label="ФИО"
      value-key="name"
      value-template="{name}"
      title-template="{name}"
      description-template="Телефон:{phone}"
      :rules="[formRules.required]"
      :disabled="disabled || partDisabled"
      :clear-value-after-blur="false"
      :items="contactList"
      required
      class="mt-15"
      @change="onChangeContact"
    />

    <vz-phone-list
      v-model="orderForm.customer[direction][type].phone"
      :rules="[formRules.required, formRules.phone]"
      :disabled="disabled"
      :crutch-country="countrySelector"
    />

    <vz-input
      v-if="useEmail"
      v-model.trim="email"
      class="mb-15 mt-15"
      big
      label="Email"
      :rules="emailRules"
      :required="isPayer && !disabled"
      :disabled="disabled"
    />
  </div>
</template>

<script setup lang="ts">
import VzInput from "~/components/ui-kit/vz-input.vue";
import {PropType} from 'vue';
import {innValidator} from '~/common/validators';
import formRules from '~/common/vz-form-rules'
import { DirectionName, ValidationRule} from '~/types/types'
import useMembers from "~/composables/useMembers";
import VzButton from "~/components/ui-kit/vz-button.vue";
import {useOrderStore} from "~/stores/order";
import VzAutocomplete from "~/components/ui-kit/vz-autocomplete.vue";
import {asyncComputed} from "@vueuse/core";
import {getTradingNetworkList} from "~/api/trading-network";
import ContactAutocomplete from '~/components/autocomplete/contact-autocomplete.vue';
import {ContractListPostData} from '~/types/api';
import VzPhoneList from '~/components/ui-kit/vz-phone-list.vue';
import {getContractorContacts} from "~/api/contractor";
import {storeToRefs} from 'pinia';
import {useUserStore} from '~/stores/personal/user';
import {useFocusWithin} from '@vueuse/core';

const props = defineProps({
  direction: {
    type: String as PropType<DirectionName>,
    default: 'dispatch'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  partDisabled: {
    type: Boolean,
    default: false
  },
  useEmail: {
    type: Boolean,
    default: true
  },
  // todo remove
  isOrderPage: {
    type: Boolean,
    default: true
  },
})

const {
  type,
  orderForm,
  countrySelector,
  customer,
  isPayer,
  phones,
  email,
  onChangeContractor,
  showSameContractorToast,
} = useMembers(props)
const {isAuth} = storeToRefs(useUserStore())

const searchParams: ContractListPostData = {
  entity: 'corporation',
  limit: 20,
  page: 1,
  useContact: true
}

const networkValidation = {
  cb: (value: string, param = '', customMessage?: string) => '',
  customMessage: '',
  param: null
}

const {setOrderType} = useOrderStore()
const innAutocomplete = ref()
const { focused } = useFocusWithin(innAutocomplete)

const rules = ref([formRules.required])

const inn = computed({
  get(): string {
    return orderForm.value.customer[props.direction].corporation.taxId || ''
  },
  set(value: string) {
    clearProjectShowToast()
    orderForm.value.customer[props.direction].corporation.taxId = value
  }
})

const innLabel = computed((): string => {
  if (countrySelector.value === 'BY') {
    return 'УНП'
  } else if (countrySelector.value === 'KZ') {
    return 'БИН'
  }
  return 'ИНН'
})

const iNNorBINorUNPvalidators = computed((): ValidationRule[] => {
  return [
    {
      cb: formRules.required.cb
    },
    {
      cb: innValidator,
      param: countrySelector.value
    },
    {
      cb: formRules.maxLength.cb,
      param: taxIdLength.value

    }
  ]
})

const kpp = computed({
  get() {
    return orderForm.value.customer[props.direction].corporation.IEC
  },
  set(value: string) {
    clearProjectShowToast()
    orderForm.value.customer[props.direction].corporation.IEC = value
  }
})

const needShowNetworkInfo = asyncComputed(async () => {
  if (props.direction === 'dispatch' || orderForm.value.meta?.guid || useRoute().name === 'order-light') {
    return false;
  }

  if (inn.value.length < 6) {
    return false;
  }

  const tradingNetwork = await getTradingNetworkList({taxId: inn.value || ''})

  if (rules.value.length === 2) {
    rules.value.pop()
  }

  if (!!tradingNetwork[0]?.guid) {
    rules.value.push(networkValidation)
  }
  return !!tradingNetwork[0]?.guid;
})

const taxIdLength = computed((): number => {
  if (countrySelector.value === 'BY') {
    return 9
  }
  if (countrySelector.value === 'AM') {
    return 8
  }
  if (countrySelector.value === 'KG') {
    return 14
  }
  return 12
})

const companyName = computed({
  get() {
    return customer.value[props.direction].corporation.companyName.value
  },
  set(value: string) {
    clearProjectShowToast()
    orderForm.value.customer[props.direction].corporation.companyName.value = value
  }
})

const name = computed({
  get(): string {
    return orderForm.value.customer[props.direction].corporation.name
  },
  set(value: string) {
    orderForm.value.customer[props.direction].corporation.name = value
  }
})

const contactList = computed(() => {
  return orderForm.value.customer[props.direction].corporation.contactList
})

const isINN = computed((): boolean => {
  return countrySelector.value === 'RU'
})

const emailRules = computed((): ValidationRule[] => {
  return (isPayer.value && !props.disabled) ? [formRules.email, formRules.required] : [formRules.email]
})

function clearProjectShowToast() {
  if (orderForm.value.customer.projectGuid && orderForm.value.customer.payer === props.direction) {
    // сбрасываем проект, если произошли изменения в контрагенте
    orderForm.value.customer.projectGuid = null;
    // useNuxtApp().$showToast({
    //   title: 'Проект стал не активен!',
    //   text: 'Проект сбросился из-за изменения компании',
    //   variant: 'warning',
    //   autocloseDelay: 10000
    // })
  }
}

function onChangeContact(_: any, {phone, email}: { phone: string, email: string }): void {
  phones.value[0] = phone
  orderForm.value.customer[props.direction][type.value].email = email
  orderForm.value.customer[props.direction][type.value].phone = phones.value
}

function changeOrderType() {
  setOrderType('networking', true)
  window.scrollTo({top: 0, behavior: 'smooth'})
}

watch(() => orderForm.value.customer[props.direction].corporation.companyName.id, async (value) => {
  if (value && isAuth.value && !orderForm.value.customer[props.direction].corporation.contactList) {
    orderForm.value.customer[props.direction].corporation.contactList = await getContractorContacts(value)
  }
}, { immediate: true })

watch(() => focused.value, () => {
  // Костыль для vueuse https://github.com/vueuse/vueuse/issues/3749
})

defineExpose({
  clearProjectShowToast
})
</script>
